import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, receiptUrl, receiptPreviewUrl } from "@/lib/api";
import { DateConfirmBanner, dateFlagMessage, dateConfirmRefusal, useDateConfirm } from "@/components/DateConfirmBanner";
import { useAuth } from "@/context/AuthContext";
import { PageHelpPanel } from "@/components/PageHelpPanel";
import { Card, Btn, PageHeader, EmptyState, InfoTip } from "@/components/common";
import { fmtDate, CURRENCY_FIELD_HELP, money, STATUS_META } from "@/lib/format";
import { CurrencySelect } from "@/components/CurrencySelect";
import { UnifiedDateInput } from "@/components/UnifiedDateInput";
import { ReceiptViewer } from "@/components/ReceiptViewer";
import { AttachmentButton, AttachmentPanel, useAttachmentPanel } from "@/components/AttachmentView";
import { useParties } from "@/lib/parties";
import { usePaymentSources, psLabel, psById, psOwnerName } from "@/lib/payments";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { curCode } from "@/lib/currencies";
import { readAmount } from "@/lib/uploads";
import {
  CheckCircle, Warning, ArrowCounterClockwise, XCircle, CaretDown, CaretUp,
  Receipt, Eye, Funnel, CheckSquare, Info,
} from "@phosphor-icons/react";
import { MoneyInput } from "@/lib/accountingFormat";
import { useStudentFromAddress } from "@/lib/studentFromAddress";
import { SchoolEnrollmentBox, useSchoolChoices, useFollowThePick, namedSchool } from "@/components/SchoolEnrollmentBox";
import { NotesField } from "@/components/NotesField";

export default function Approvals() {
  const qc = useQueryClient();
  const { selectedStudentId } = useAuth();
  // "Review now" in the approval email opens this page on the beneficiary it
  // names. Until that name is checked the list is held back, so the page never
  // shows everyone's work first and then jumps. (ZEN-697)
  const { settling } = useStudentFromAddress();
  const isAllView = !selectedStudentId;
  const { data: queue = [], isLoading: queueLoading } = useQuery({
    queryKey: ["submissions-queue", selectedStudentId || "all"],
    queryFn: async () => (await api.get(`/submissions/queue${selectedStudentId ? `?student_id=${selectedStudentId}` : ""}`)).data,
    refetchOnWindowFocus: true,
    enabled: !settling,
  });
  const isLoading = queueLoading || settling;
  const { data: students = [] } = useQuery({
    queryKey: ["students"],
    queryFn: async () => (await api.get("/students")).data,
  });
  const [filter, setFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [shown, setShown] = useState(20);
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);

  const awaiting = queue.filter(r => r.submission_status === "awaiting_owner_approval");
  const waitingOnStudent = queue.filter(r => r.submission_status === "needs_student_review" || r.submission_status === "returned");

  const studentNames = [...new Set(queue.map(r => r.student_name).filter(Boolean))];
  const filtered = filter === "all" ? awaiting : awaiting.filter(r => r.student_name === filter);

  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const selectAll = () => {
    const clean = filtered.filter(r => !r.is_duplicate);
    setSelectedIds(new Set(clean.map(r => r.id)));
  };

  const bulkApprove = async () => {
    if (!selectedIds.size) return;
    setBulkBusy(true);
    let ok = 0;
    let dateAsks = 0;
    let firstDateAsk = null;
    for (const rid of selectedIds) {
      try {
        await api.post(`/submissions/${rid}/approve`, {});
        ok++;
      } catch (e) {
        // A date waiting on "Yes, this date is correct." is not a failure —
        // it is a question, and swallowing it left receipts looking stuck
        // for no stated reason. (ZEN-162, pre-merge diff review.)
        const refusal = dateConfirmRefusal(e);
        if (refusal) { dateAsks++; if (!firstDateAsk) firstDateAsk = refusal; }
      }
    }
    const failed = selectedIds.size - ok - dateAsks;
    toast.success(`${ok} receipt${ok === 1 ? "" : "s"} approved — now under “${STATUS_META.draft.label}” on the Expenses page`);
    // SHORT ON PURPOSE. This used to quote the full date sentence inside a
    // second sentence, so it said "confirm or correct the date" twice and ran
    // two dashes into one breath — read while doing a bulk action, where the
    // short version is what is wanted. The full explanation is waiting on the
    // receipt itself. (Maryse 2026-08-30.)
    if (dateAsks > 0) toast.warning(
      `${dateAsks === 1 ? "One receipt needs" : `${dateAsks} receipts need`} a date check — `
      + `open ${dateAsks === 1 ? "it" : "each one"} to confirm or correct the date.`);
    // SAY SO WHEN SOME DID NOT GO THROUGH. The loop used to swallow every
    // failure and report only the successes, so a receipt that refused to
    // approve simply stayed in the queue with nothing said about it.
    if (failed > 0) toast.error(`${failed} could not be approved — ${failed === 1 ? "it is" : "they are"} still in the queue`);
    setSelectedIds(new Set());
    refresh();
    setBulkBusy(false);
  };

  // WHAT HAS TO BE RE-READ AFTER ANYTHING ON THIS SCREEN CHANGES. These two
  // lines used to refresh the list and a separate counting door that NOTHING
  // EVER FETCHED — while the number beside "Approvals" on the menu, which is
  // the one a family actually reads, was never refreshed at all. So approving
  // the last submission emptied the page and left the menu still claiming work
  // was waiting. The dead door is deleted; this asks for the number the menu
  // really shows. (ZEN-687)
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["submissions-queue"] });
    qc.invalidateQueries({ queryKey: ["attention"] });
  };

  return (
    <div>
      <PageHeader title="Approvals" subtitle="Check the facts on receipts your beneficiaries sent, and approve them into your records. You decide whether each one is 529-eligible afterwards, on the Expenses page." />
      <PageHelpPanel pageKey="approvals" />

      {/* Filters + bulk actions */}
      {awaiting.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="approval-toolbar">
          {isAllView && <>
            <Funnel size={16} className="text-[#6b7280]" />
            <select value={filter} onChange={e => setFilter(e.target.value)}
              className="h-8 rounded-md border border-[#C2C9D1] px-2 text-xs" data-testid="approval-filter-student">
              <option value="all">All beneficiaries</option>
              {studentNames.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </>}
          <span className="text-xs text-[#6b7280]">{filtered.length} awaiting approval</span>
          <div className="ml-auto flex items-center gap-2">
            {selectedIds.size > 0 && (
              <>
                <Btn onClick={bulkApprove} disabled={bulkBusy} data-testid="bulk-approve-btn">
                  <CheckCircle size={16} weight="bold" /> Approve {selectedIds.size} selected
                </Btn>
                <Btn onClick={() => setBulkRejectOpen(true)} disabled={bulkBusy} className="!bg-[#F01919] hover:!bg-[#c91010]" data-testid="bulk-reject-btn">
                  <XCircle size={16} weight="bold" /> Reject {selectedIds.size} selected
                </Btn>
              </>
            )}
            <button onClick={selectAll} className="text-xs font-semibold text-[#034AA2] hover:underline" title="Selects loaded items only" data-testid="select-all-btn">
              <CheckSquare size={14} weight="bold" className="inline mr-0.5" /> Select all clean
            </button>
          </div>
        </div>
      )}

      {/* Main queue */}
      {filtered.length > 0 ? (
        <div className="mt-3 space-y-3" data-testid="approval-list">
          {filtered.slice(0, shown).map(r => (
            <ApprovalRow key={r.id} receipt={r} students={students}
              expanded={expandedId === r.id} onToggle={() => setExpandedId(expandedId === r.id ? null : r.id)}
              selected={selectedIds.has(r.id)} onSelect={() => toggleSelect(r.id)}
              onDone={refresh} />
          ))}
          {shown < filtered.length && (
            <button onClick={() => setShown(s => s + 20)}
              className="mx-auto mt-2 block rounded-md border border-[#C2C9D1] px-4 py-2 text-xs font-semibold text-[#034AA2] hover:bg-[#E9E9EB] transition-colors" data-testid="approvals-show-more">
              Show more ({Math.min(20, filtered.length - shown)} of {filtered.length - shown} remaining)
            </button>
          )}
        </div>
      ) : !isLoading && awaiting.length === 0 ? (
        <EmptyState icon={Receipt} title="No pending approvals" subtitle="Beneficiary receipts will appear here once submitted." className="mt-6" />
      ) : null}

      {/* Waiting on student (collapsed) */}
      {waitingOnStudent.length > 0 && (
        <WaitingOnStudentSection items={waitingOnStudent} />
      )}

      <BulkRejectDialog open={bulkRejectOpen} onClose={() => setBulkRejectOpen(false)}
        ids={[...selectedIds]} onDone={() => { setSelectedIds(new Set()); refresh(); }} />
    </div>
  );
}


/**
 * A submission always has exactly one thing attached — the receipt itself —
 * so this is a one-item list rather than a general one. A submission that
 * arrived as the TEXT of an email has no file, and the panel says so and
 * shows the text; the paperclip is still there because there is still
 * something to read.
 */
function submissionAttachments(receipt) {
  // No stored file means it came in as the text of an email. The panel reads
  // what the server returns rather than guessing from a channel flag, and the
  // receipt route hands back that text; marking it as a receipt would send it
  // down the picture-per-page path, which has no picture to make.
  const hasFile = !!receipt.storage_path;
  return [{
    key: receipt.id,
    label: receipt.original_filename || "Receipt",
    fileUrl: receiptUrl(receipt.id),
    receiptId: hasFile ? receipt.id : undefined,
    pageCount: receipt.page_count || 1,
  }];
}

export function submissionLabel(receipt, draft) {
  const who = receipt.student_name || "";
  const what = draft?.payee || receipt.original_filename || "submission";
  const amount = readAmount(draft) != null
    ? `, ${money(Number(readAmount(draft)), curCode(draft.original_currency, "USD"))}`
    : "";
  return `${who ? `${who} — ` : ""}${what}${amount}`;
}

function ApprovalRow({ receipt, expanded, onToggle, selected, onSelect, onDone, students }) {
  const draft = receipt.student_draft || receipt.draft || {};
  const ocrDraft = receipt.draft || {};
  const [busy, setBusy] = useState(false);
  const [returnDlg, setReturnDlg] = useState(false);
  const [rejectDlg, setRejectDlg] = useState(false);
  // Working through a batch means looking at receipt after receipt. Opening
  // each submission for editing to see one is the dig this ticket exists to
  // remove, so the paperclip on the row opens the receipt on its own —
  // underneath the row, with the beneficiary, the payee, the date and the
  // amount still on screen beside it. (ZEN-351)
  const attachPanel = useAttachmentPanel();
  const { data: parties } = useParties();
  const { data: psData } = usePaymentSources(true);
  const allSources = psData?.sources || [];
  const visibleSources = allSources.filter(s => !s.archived || s.id === draft.payment_source_id);
  const studentPaymentSource = draft.payment_source_id ? psById(allSources, draft.payment_source_id) : null;
  const student = (students || []).find(s => s.id === receipt.student_id);

  // Editable form — includes all classification fields from ExpenseDialog
  // Prefill payor from student_draft when available; fallback "" for legacy
  const [f, setF] = useState({
    transaction_date: draft.transaction_date || "",
    payee: draft.payee || "",
    description: draft.description || "",
    original_currency: curCode(draft.original_currency, "USD"),
    original_amount: readAmount(draft) ?? "",
    // NO CLASSIFICATION KEYS HERE, and that is load-bearing: the approve call
    // below spreads this whole object, so leaving `eligible_529: true` in place
    // would keep sending "yes, 529-eligible" on every approval even with the
    // menu gone — which is the exact defect this ticket removes. (ZEN-114)
    payor_type: draft.payor_type || "",
    payor_party_id: draft.payor_party_id || "",
    payment_source_id: draft.payment_source_id || null,
    // The server's pick for the date paid, filled in below (ZEN-460).
    school_enrollment_id: "",
    notes: "",
  });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  // THE SCHOOL: picked from the date paid, changeable only to a school that
  // could apply to that year — the same box and rule as the expense form.
  // (ZEN-460, Maryse 15 Oct 2026)
  const schoolChosen = useRef(false);
  const { data: schoolChoices } = useSchoolChoices({
    studentId: receipt.student_id, date: f.transaction_date, schoolId: namedSchool(schoolChosen, false, f.school_enrollment_id) });
  const chooseSchool = useFollowThePick({
    data: schoolChoices, value: f.school_enrollment_id,
    setValue: (id) => set("school_enrollment_id", id), resetKey: receipt.id, keepStored: false, chosenRef: schoolChosen });
  // ZEN-162: the server's verdict on the date about to be approved — from the
  // queue row on load, or from a refused approval. The tick lifts it; editing
  // the date clears it for the server to re-judge.
  const dc = useDateConfirm(receipt.draft_date_flag);
  React.useEffect(() => { dc.resetFor(receipt.id, receipt.draft_date_flag); }, [receipt.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async () => {
    if (!f.payor_type) { toast.error("Select who paid"); return; }
    setBusy(true);
    try {
      await api.post(`/submissions/${receipt.id}/approve`, {
        ...f,
        original_amount: parseFloat(f.original_amount) || 0,
        payor_type: f.payor_type,
        payor_party_id: f.payor_type === "party" ? f.payor_party_id : null,
        paid_by: ["student", "owner", "529"].includes(f.payor_type) ? f.payor_type : "owner",
        payment_source_id: f.payment_source_id || null,
        school_enrollment_id: namedSchool(schoolChosen, false, f.school_enrollment_id) || null,
        notes: f.notes || "",
        date_confirmed: dc.confirmed,
      });
      toast.success(`Approved: ${f.payee} — added to “${STATUS_META.draft.label}” on the Expenses page`);
      onDone();
    } catch (e) {
      if (!dc.applyRefusal(e)) toast.error(apiErr(e));
    }
    finally { setBusy(false); }
  };

  // Diff hints: where student changed OCR values
  const diffs = [];
  if (ocrDraft.payee && draft.payee && ocrDraft.payee !== draft.payee) diffs.push("payee");
  if (ocrDraft.original_amount != null && draft.original_amount != null && Math.abs(Number(ocrDraft.original_amount) - Number(draft.original_amount)) > 0.01) diffs.push("amount");
  if (ocrDraft.transaction_date && draft.transaction_date && ocrDraft.transaction_date !== draft.transaction_date) diffs.push("date");

  return (
    <div className={`rounded-lg border ${receipt.is_duplicate ? "border-[#D97706]/50 bg-[#FEF3C7]/20" : selected ? "border-[#034AA2]/50 bg-[#034AA2]/5" : "border-[#C2C9D1] bg-white"} overflow-hidden`}
      data-testid={`approval-row-${receipt.id}`}>
      {/* Summary row */}
      {/* THE ROW WRAPS INSTEAD OF DRAWING OVER ITSELF. Up to five status tags
          follow the name and the payee, and every one of them refuses to
          shrink — so on a phone they had nowhere to go and were painted on top
          of the beneficiary's name and the payee. Allowing the row to wrap
          gives them a second line; giving the text block a sensible minimum
          width (basis) decides when that happens, rather than leaving it to
          whatever the longest payee happens to be. (ZEN-169) */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2.5 cursor-pointer" onClick={onToggle}>
        <input type="checkbox" checked={selected} onChange={(e) => { e.stopPropagation(); onSelect(); }}
          className="shrink-0 accent-[#034AA2]" data-testid={`approval-check-${receipt.id}`}
          onClick={e => e.stopPropagation()} />
        <span onClick={(e) => e.stopPropagation()} className="shrink-0">
          <AttachmentButton
            ref={attachPanel.buttonRef(receipt.id)}
            attachments={submissionAttachments(receipt)}
            recordLabel={submissionLabel(receipt, draft)}
            expanded={attachPanel.isOpen(receipt.id)}
            panelId={attachPanel.panelId(receipt.id)}
            onToggle={() => attachPanel.toggle(receipt.id)}
            testid={`approval-attach-btn-${receipt.id}`}
          />
        </span>
        <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-[#C2C9D1] bg-[#F8FAFC]">
          <img src={receiptUrl(receipt.id)} alt="" className="h-full w-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
        </div>
        <div className="min-w-0 flex-1 basis-[11rem]">
          {/* …and the two names truncate rather than pushing out of the block
              they live in, which is how they reached the tags in the first
              place. A long payee now ends in an ellipsis; the full text is
              still there when the row is expanded. */}
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
            <span className="max-w-full truncate text-xs font-bold text-[#034AA2]">{receipt.student_name}</span>
            <span className="max-w-full truncate text-xs font-semibold text-[#1f2937]" title={draft.payee || receipt.original_filename}>{draft.payee || receipt.original_filename}</span>
          </div>
          <div className="text-[11px] text-[#6b7280]">
            {draft.transaction_date && fmtDate(draft.transaction_date)}
            {readAmount(draft) != null && <> · {money(Number(readAmount(draft)), curCode(draft.original_currency, "USD"))}</>}
            {/* Legacy breadcrumb: receipts submitted before ZEN-114 still carry the
                category their sender picked. New submissions have none — the
                account owner classifies on the Expenses page. */}
            {draft.category && <> · {draft.category}</>}
          </div>
        </div>
        {diffs.length > 0 && (
          <span className="shrink-0 rounded-full bg-[#034AA2]/10 px-2 py-0.5 text-[10px] font-bold text-[#034AA2]">
            Beneficiary edited: {diffs.join(", ")}
          </span>
        )}
        {receipt.is_duplicate && (
          <span className="shrink-0 rounded-full bg-[#D97706]/10 px-2 py-0.5 text-[10px] font-bold text-[#D97706]">
            <Warning size={10} weight="bold" className="inline mr-0.5" /> Possible duplicate
          </span>
        )}
        {draft.payor_type && (
          <span className="shrink-0 rounded-full bg-[#059669]/10 px-2 py-0.5 text-[10px] font-bold text-[#059669]" data-testid="paid-by-chip">
            Paid by: {draft.payor_type === "student" ? "Student" : draft.payor_type === "owner" ? (parties?.owner?.nickname || "Owner") : draft.payor_type === "party" ? ((parties?.custom || []).find(p => p.id === draft.payor_party_id)?.nickname || "Party") : draft.payor_type}
          </span>
        )}
        {receipt.return_count > 0 && (
          <span className="shrink-0 rounded-full bg-[#6b7280]/10 px-2 py-0.5 text-[10px] font-bold text-[#6b7280]">
            Returned {receipt.return_count}x
          </span>
        )}
        {expanded ? <CaretUp size={16} className="ml-auto shrink-0 text-[#6b7280]" /> : <CaretDown size={16} className="ml-auto shrink-0 text-[#6b7280]" />}
      </div>

      {attachPanel.isOpen(receipt.id) && (
        <div className="border-t border-[#C2C9D1] px-3 pb-3">
          <AttachmentPanel
            id={attachPanel.panelId(receipt.id)}
            attachments={submissionAttachments(receipt)}
            recordLabel={submissionLabel(receipt, draft)}
            onClose={() => attachPanel.close(receipt.id)}
            testid={`approval-attach-panel-${receipt.id}`}
          />
        </div>
      )}

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-[#C2C9D1] px-3 py-3">
          <div className="flex flex-col gap-4 lg:flex-row">
            {/* Receipt preview. A SHARE OF THE ROW, not a fixed 16rem. At a
                fixed 256px the receipt opened at 20% of life size and could not
                be read, while the fields beside it ran to 854px on a 1440-wide
                laptop and 1334px on a 1920 screen — a date box nearly 600px
                across. Measured on the running app; the receipt is the thing
                being checked, so it gets the room. (ZEN-666) */}
            <div className="shrink-0 lg:w-2/5">
              <ReceiptViewer key={receipt.id} getPageSrc={(page) => receiptPreviewUrl(receipt.id, page)} pageCount={receipt.page_count || 1} />
            </div>
            {/* Editable form */}
            <div className="flex-1 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <label className="block text-xs font-semibold text-[#6b7280]">Date
                <div className="mt-0.5"><UnifiedDateInput value={f.transaction_date} onChange={v => { set("transaction_date", v); dc.clear(); }} testid={`appr-date-${receipt.id}`} /></div>
                <DateConfirmBanner message={dateFlagMessage(dc.flag, "owner")}
                  confirmed={dc.confirmed} onConfirmedChange={dc.setConfirmed}
                  testid={`appr-date-confirm-${receipt.id}`} />
              </label>
              <label className="block text-xs font-semibold text-[#6b7280]">Payee
                <input value={f.payee} onChange={e => set("payee", e.target.value)}
                  className="mt-0.5 block h-9 w-full rounded-md border border-[#C2C9D1] px-2 text-sm" data-testid={`appr-payee-${receipt.id}`} />
              </label>
              <label className="block text-xs font-semibold text-[#6b7280]"><span className="inline-flex items-center gap-1">Currency <InfoTip testid={`appr-currency-info-${receipt.id}`} text={CURRENCY_FIELD_HELP} /></span>
                <div className="mt-0.5"><CurrencySelect value={f.original_currency} onChange={v => set("original_currency", v)} testid={`appr-currency-${receipt.id}`} className="!h-9" /></div>
              </label>
              <label className="block text-xs font-semibold text-[#6b7280]">Amount
                <MoneyInput currency={curCode(f.original_currency)} className="mt-0.5 !h-9" value={f.original_amount} onChange={v => set("original_amount", v)}
                  placeholder="" testid={`appr-amount-${receipt.id}`} />
              </label>
              <label className="col-span-1 sm:col-span-2 block text-xs font-semibold text-[#6b7280]">Description
                <input value={f.description} onChange={e => set("description", e.target.value)}
                  className="mt-0.5 block h-9 w-full rounded-md border border-[#C2C9D1] px-2 text-sm" data-testid={`appr-desc-${receipt.id}`} />
              </label>
              <div className="col-span-1 sm:col-span-2 flex items-start gap-2 rounded-lg border border-[#034AA2]/25 bg-[#034AA2]/5 px-3 py-2" data-testid={`appr-classify-note-${receipt.id}`}>
                <Info size={15} weight="fill" className="mt-0.5 shrink-0 text-[#034AA2]" />
                <p className="text-[11px] text-[#374151]">
                  <span className="font-bold">Approving records the receipt — it doesn&rsquo;t decide anything about tax.</span>{" "}
                  The expense lands under &ldquo;{STATUS_META.draft.label}&rdquo; on the Expenses page, where you say whether it&rsquo;s
                  529-eligible and which category it belongs to. Until you do, it counts toward nothing.
                </p>
              </div>
              {/* Paid by + Payment account — side by side, as on the expense form (ZEN-795) */}
              <div data-testid={`appr-paidby-block-${receipt.id}`}>
                <span className="text-xs font-semibold text-[#6b7280]">Paid by</span>
                <div className="mt-0.5 flex flex-wrap gap-1.5">
                  {["student", "owner", "529"].map(pt => (
                    <button key={pt} type="button" onClick={() => { set("payor_type", pt); set("payor_party_id", ""); }}
                      className={`h-8 rounded-md px-3 text-xs font-semibold transition-colors ${f.payor_type === pt ? "bg-[#034AA2] text-white" : "border border-[#C2C9D1] bg-white hover:bg-[#E9E9EB]"}`}
                      data-testid={`appr-paidby-${pt}-${receipt.id}`}>
                      {pt === "student" ? "Beneficiary" : pt === "owner" ? "Owner" : "529 Direct"}
                    </button>
                  ))}
                  {(parties?.custom || []).map(p => (
                    <button key={p.id} type="button" onClick={() => { set("payor_type", "party"); set("payor_party_id", p.id); }}
                      className={`h-8 rounded-md px-3 text-xs font-semibold transition-colors ${f.payor_type === "party" && f.payor_party_id === p.id ? "bg-[#034AA2] text-white" : "border border-[#C2C9D1] bg-white hover:bg-[#E9E9EB]"}`}
                      data-testid={`appr-paidby-party-${p.id}-${receipt.id}`}>
                      {p.nickname}
                    </button>
                  ))}
                </div>
                {studentPaymentSource && (
                  <div className="mt-1.5 text-[11px] text-[#6b7280]" data-testid={`appr-student-ps-${receipt.id}`}>
                    Beneficiary says paid from: <span className="font-semibold text-[#374151]">{psLabel(studentPaymentSource, psOwnerName(studentPaymentSource, students, parties))}</span>
                  </div>
                )}
              </div>
              <label className="block text-xs font-semibold text-[#6b7280]">Payment account
                <select value={f.payment_source_id || ""} onChange={e => set("payment_source_id", e.target.value || null)}
                  className="mt-0.5 block h-9 w-full rounded-md border border-[#C2C9D1] px-2 text-sm" data-testid={`appr-ps-${receipt.id}`}>
                  <option value="">— None —</option>
                  {visibleSources.map(s => <option key={s.id} value={s.id}>{psLabel(s, psOwnerName(s, students, parties))}</option>)}
                </select>
              </label>
              {/* NO TAX-YEAR FIELD. This is the exact control the live defect came
                  through: the reader mis-read a receipt's year as 2023, the
                  account owner corrected the DATE here to 2026-08-04, and this input still
                  held 2023 and posted it. The date decides the year now, on the
                  server, so the year is a fact to state rather than a box to fill.
                  (ZEN-172) */}
              <div className="text-xs text-[#6b7280]" data-testid={`appr-taxyear-${receipt.id}`}>
                Counts toward tax year{" "}
                <span className="font-semibold text-[#374151]">
                  {(f.transaction_date || "").slice(0, 4) || "—"}
                </span>
                <span className="block text-[10px] text-[#9aa1a9]">set by the date paid</span>
              </div>
              <div className="sm:col-start-1">
                <SchoolEnrollmentBox choices={schoolChoices?.choices} value={f.school_enrollment_id}
                  onChange={(v) => { chooseSchool(); set("school_enrollment_id", v); }}
                  studentName={student ? (student.nickname || (student.full_name || "").split(" ")[0]) : ""}
                  year={(f.transaction_date || "").slice(0, 4)} testid={`appr-school-${receipt.id}`} />
              </div>
              <NotesField className="col-span-1 sm:col-span-2" value={f.notes} onChange={(v) => set("notes", v)} testid={`appr-notes-${receipt.id}`} />
            </div>
          </div>
          {/* Actions */}
          <div className="mt-3 flex items-center gap-2 border-t border-[#C2C9D1] pt-3">
            <Btn onClick={approve} disabled={busy} data-testid={`appr-approve-${receipt.id}`}>
              {busy ? "Approving…" : <><CheckCircle size={16} weight="bold" /> Approve</>}
            </Btn>
            <Btn variant="ghost" onClick={() => setReturnDlg(true)} data-testid={`appr-return-${receipt.id}`}>
              <ArrowCounterClockwise size={16} weight="bold" /> Return
            </Btn>
            <Btn variant="ghost" onClick={() => setRejectDlg(true)} className="text-[#F01919] hover:bg-[#F01919]/10" data-testid={`appr-reject-${receipt.id}`}>
              <XCircle size={16} weight="bold" /> Reject
            </Btn>
          </div>
        </div>
      )}

      <ReturnDialog open={returnDlg} onClose={() => setReturnDlg(false)} receiptId={receipt.id} onDone={onDone} />
      <RejectDialog open={rejectDlg} onClose={() => setRejectDlg(false)} receiptId={receipt.id} onDone={onDone} />
    </div>
  );
}


function ReturnDialog({ open, onClose, receiptId, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!note.trim()) { toast.error("A note is required"); return; }
    setBusy(true);
    try {
      await api.post(`/submissions/${receiptId}/return`, { note: note.trim() });
      toast.success("Returned to beneficiary");
      onClose();
      onDone();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Return to beneficiary</DialogTitle></DialogHeader>
        <label className="block text-xs font-semibold text-[#6b7280]">Note for beneficiary (required)
          <textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="What needs to be fixed?"
            className="mt-1 block w-full rounded-md border border-[#C2C9D1] px-2 py-1.5 text-sm resize-none" data-testid="return-note-input" />
        </label>
        <DialogFooter className="mt-2">
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={submit} disabled={busy || !note.trim()} data-testid="return-confirm-btn">
            <ArrowCounterClockwise size={16} weight="bold" /> Return
          </Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function RejectDialog({ open, onClose, receiptId, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    setBusy(true);
    try {
      await api.post(`/submissions/${receiptId}/reject`, { reason: reason.trim() });
      toast.success("Receipt rejected");
      onClose();
      onDone();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Reject receipt</DialogTitle></DialogHeader>
        {/* WORDING IS THE OWNER'S, approved 2026-09-11 (ZEN-587). What it
            replaced was untrue as of that ruling: "permanently rejected" — the
            file stays usable and can be attached to an expense later — and
            "the file is retained", which is plumbing a beneficiary does not
            need. Her frame: the only things that matter here are that she has
            not approved it, and that the person on the other end learns why
            and what to do. Do not reword without her. */}
        <p className="text-sm text-[#374151]">This submission is not approved, so no expense will be created. Tell your beneficiary why, and what to do next.</p>
        <label className="block text-xs font-semibold text-[#6b7280]">Reason (required)
          <textarea rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="Your reason — and what they should do next"
            className="mt-1 block w-full rounded-md border border-[#C2C9D1] px-2 py-1.5 text-sm resize-none" data-testid="reject-reason-input" />
        </label>
        <DialogFooter className="mt-2">
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={submit} disabled={busy || !reason.trim()} className="bg-[#F01919] text-white hover:bg-[#c91010]" data-testid="reject-confirm-btn">
            <XCircle size={16} weight="bold" /> Reject
          </Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function WaitingOnStudentSection({ items }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-6" data-testid="waiting-on-student-section">
      <button onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 rounded-lg bg-[#F8FAFC] px-3 py-2 text-left text-xs font-bold uppercase tracking-wide text-[#6b7280] hover:bg-[#E9E9EB]">
        {open ? <CaretUp size={14} /> : <CaretDown size={14} />}
        Waiting on beneficiary ({items.length})
      </button>
      {open && (
        <div className="mt-2 space-y-1.5">
          {items.map(r => {
            const draft = r.draft || {};
            return (
              <div key={r.id} className="flex items-center gap-3 rounded-md border border-[#E9E9EB] bg-white px-3 py-2">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded border border-[#C2C9D1] bg-[#F8FAFC]">
                  <img src={receiptUrl(r.id)} alt="" className="h-full w-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-xs font-bold text-[#034AA2]">{r.student_name}</span>
                  <span className="ml-2 text-xs text-[#1f2937]">{draft.payee || r.original_filename}</span>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${r.submission_status === "returned" ? "bg-[#F01919]/10 text-[#F01919]" : "bg-[#D97706]/10 text-[#92400E]"}`}>
                  {r.submission_status === "returned" ? "Returned" : "Needs review"}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function BulkRejectDialog({ open, onClose, ids, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    if (!reason.trim()) { toast.error("Enter a reason"); return; }
    setBusy(true);
    let ok = 0;
    for (const rid of ids) {
      try {
        await api.post(`/submissions/${rid}/reject`, { reason: reason.trim() });
        ok++;
      } catch { /* skip */ }
    }
    toast.success(`${ok} receipt${ok === 1 ? "" : "s"} rejected`);
    setBusy(false);
    setReason("");
    onClose();
    onDone?.();
  };
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Reject {ids.length} receipt{ids.length === 1 ? "" : "s"}?</DialogTitle></DialogHeader>
        <p className="text-sm text-[#374151]">Enter a reason that will be applied to all selected receipts.</p>
        {/* Same prompt as the single dialog above: this reason goes to the
            same door and the beneficiary reads it the same way. (ZEN-587) */}
        <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Your reason — and what they should do next"
          className="mt-2 block w-full rounded-md border border-[#C2C9D1] px-3 py-2 text-sm" rows={3} data-testid="bulk-reject-reason" />
        <DialogFooter className="mt-2">
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={confirm} disabled={busy || !reason.trim()} className="bg-[#F01919] text-white hover:bg-[#c91010]" data-testid="bulk-reject-confirm">
            {busy ? "Rejecting…" : `Reject ${ids.length}`}
          </Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
